import app from 'flarum/forum/app';
import FormModal from 'flarum/common/components/FormModal';
import Button from 'flarum/common/components/Button';
import Switch from 'flarum/common/components/Switch';
import classList from 'flarum/common/utils/classList';
import TitlePicker from './TitlePicker';
import ScopePicker from './ScopePicker';
import { t, text, img, sourceName, kindForTags, linkFor, pickFromMedia, titleFor } from '../util';
import { loadSeason } from '../api';

/**
 * Change what a posted discussion is linked to: pick a different title (and,
 * for a show, a season or episode), or remove the link. Optionally renames the
 * discussion to match. Uses the same permission as renaming.
 */
export default class LinkModal extends FormModal {
  oninit(vnode) {
    super.oninit(vnode);
    const discussion = this.attrs.discussion;
    const current = linkFor(discussion);
    this.kind = kindForTags(discussion.tags()) || current?.media.kind();
    this.link = current ? {
      ...pickFromMedia(current.media),
      season: current.season,
      episode: current.episode ? {
        n: current.episode
      } : null
    } : null;
    this.current = current;
    this.nameEpisode();

    // The rest of a series, shown as a row on the card (picked by hand).
    this.related = (discussion.marqueeRelatedMedia() || []).filter(Boolean).map(pickFromMedia);
    this.relatedQuery = '';
    this.query = '';
    this.rename = true;
    this.saving = false;

    // Each title's background: 'none' or an uploaded image's address; a title
    // not in it has the source's. Opened from the card: the title it shows.
    this.savedBackdrops = discussion.attribute('marqueeBackdrops') || {};
    this.backdrops = {
      ...this.savedBackdrops
    };
    this.backdropFor = (this.attrs.media || current?.media)?.id();
    this.uploading = false;
    this.deleting = false;
  }

  /** The page knows the linked episode's number; its name comes from the season. */
  nameEpisode() {
    const current = this.current;
    if (!current || !current.season || !current.episode) return;
    loadSeason(current.media.id(), current.season).then(season => {
      const episode = season.episodes.find(e => e.n === current.episode);
      if (episode && this.link && this.link.episode && this.link.episode.n === episode.n) {
        this.link.episode = episode;
        m.redraw();
      }
    }, () => {});
  }
  className() {
    // Wide enough for a result's full title, developer and platforms.
    return 'MarqueeLinkModal';
  }
  title() {
    return t('link.title');
  }
  content() {
    const link = this.link;
    return m("div", {
      className: "Modal-body"
    }, m("div", {
      className: "Form"
    }, m("div", {
      className: "Form-group"
    }, link ? m("div", {
      className: "MarqueeLinkModal-current"
    }, link.poster ? m("img", {
      src: img(link.poster, 'w92'),
      alt: ""
    }) : m("span", {
      className: "Marquee-noPoster"
    }), m("span", null, m("b", null, link.year ? `${link.title} (${link.year})` : link.title), m("small", null, sourceName(link.kind))), m(Button, {
      className: "Button Button--link",
      icon: "fas fa-times",
      onclick: () => this.link = null
    }, t('link.remove'))) : m("p", {
      className: "helpText"
    }, t('link.none'))), this.kind ? m("div", {
      className: "Form-group"
    }, m("label", null, t('link.search_label')), m(TitlePicker, {
      wrapper: "div",
      value: this.query,
      oninput: value => this.query = value,
      kind: this.kind,
      linked: false,
      onpick: picked => {
        this.link = picked;
        this.nameEpisode();
        this.query = '';
      }
    })) : null, link && this.kind ? this.relatedField() : null, link && link.seasons && link.seasons.length ? m("div", {
      className: "Form-group"
    }, m(ScopePicker, {
      link: link,
      onchange: () => m.redraw()
    })) : null, link && this.current && link.externalId === pickFromMedia(this.current.media).externalId ? this.backdropField() : null, link ? m("div", {
      className: "Form-group"
    }, m(Switch, {
      state: this.rename,
      onchange: value => this.rename = value
    }, t('link.rename', {
      title: m("b", null, titleFor(link, link.season, link.episode))
    }))) : null, m("div", {
      className: "Form-group"
    }, m(Button, {
      className: "Button Button--primary Button--block",
      type: "submit",
      loading: this.saving
    }, t('link.save')))));
  }

  /** The titles on the card as saved, still on it: the linked one, then its related titles. */
  backdropTitles() {
    const kept = media => this.related.some(r => r.kind === media.kind() && r.externalId === media.externalId());
    return [this.current.media, ...(this.attrs.discussion.marqueeRelatedMedia() || []).filter(media => media && kept(media))];
  }

  /** A title's background: the source's, none, or an uploaded image. */
  backdropField() {
    const titles = this.backdropTitles();
    if (!titles.some(media => media.id() === this.backdropFor)) this.backdropFor = titles[0].id();
    const id = this.backdropFor;
    const value = this.backdrops[id] ?? null;
    const own = value && value !== 'none';
    const choice = (choice, label) => m(Button, {
      className: classList('Button', {
        active: choice === 'own' ? own : value === choice
      }),
      "aria-pressed": String(choice === 'own' ? own : value === choice),
      onclick: () => {
        if (choice === 'own') this.element.querySelector('.MarqueeLinkModal-file').click();else if (choice === null) delete this.backdrops[id];else this.backdrops[id] = choice;
      }
    }, label);
    return m("div", {
      className: "Form-group MarqueeLinkModal-backdrop"
    }, m("label", null, t('link.backdrop_label')), titles.length > 1 ? m("select", {
      className: "FormControl",
      "aria-label": text('link.backdrop_for'),
      value: id,
      onchange: e => this.backdropFor = e.target.value
    }, titles.map(media => m("option", {
      value: media.id()
    }, media.year() ? `${media.title()} (${media.year()})` : media.title()))) : null, m("div", {
      className: "ButtonGroup"
    }, choice(null, t('link.backdrop_default')), choice('none', t('link.backdrop_none')), choice('own', t('link.backdrop_own'))), own ? m("img", {
      className: "MarqueeLinkModal-backdropPreview",
      src: value,
      alt: ""
    }) : null, m("input", {
      className: "MarqueeLinkModal-file",
      type: "file",
      accept: "image/jpeg,image/png,image/webp",
      hidden: true,
      onchange: e => this.upload(e.target, id)
    }), m("div", {
      className: "MarqueeLinkModal-backdropActions"
    }, m(Button, {
      className: "Button",
      icon: "fas fa-upload",
      loading: this.uploading,
      onclick: () => this.element.querySelector('.MarqueeLinkModal-file').click()
    }, own ? t('link.backdrop_replace') : t('link.backdrop_upload')), own && this.savedBackdrops[id] === value ? m(Button, {
      className: "Button Button--danger",
      icon: "fas fa-trash-alt",
      loading: this.deleting,
      onclick: () => this.deleteImage(id)
    }, t('link.backdrop_delete')) : null), m("p", {
      className: "helpText"
    }, t('link.backdrop_help')));
  }

  /** The background as saved, now that an upload or a delete has changed it. */
  savedAs(backdrops, id) {
    this.savedBackdrops = backdrops || {};
    if (this.savedBackdrops[id]) this.backdrops[id] = this.savedBackdrops[id];else delete this.backdrops[id];
  }
  upload(input, id) {
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) return;
    const body = new FormData();
    body.append('backdrop', file);
    body.append('media', id);
    this.uploading = true;
    m.redraw();
    app.request({
      method: 'POST',
      url: `${app.forum.attribute('apiUrl')}/marquee/discussions/${this.attrs.discussion.id()}/backdrop`,
      serialize: raw => raw,
      body
    }).then(response => {
      this.savedAs(response.data.backdrops, id);
      this.attrs.discussion.pushAttributes({
        marqueeBackdrops: this.savedBackdrops
      });
      app.alerts.show({
        type: 'success'
      }, text('link.backdrop_uploaded'));
    }, () => {}).finally(() => {
      this.uploading = false;
      m.redraw();
    });
  }

  /** Deleted at once, as an upload is saved at once: the file goes with it. */
  deleteImage(id) {
    this.deleting = true;
    this.attrs.discussion.save({
      marqueeBackdrops: {
        [id]: null
      }
    }).then(() => {
      this.savedAs(this.attrs.discussion.attribute('marqueeBackdrops'), id);
      app.alerts.show({
        type: 'success'
      }, text('link.backdrop_deleted'));
    }, () => {}).finally(() => {
      this.deleting = false;
      m.redraw();
    });
  }

  /** Related titles: the other games (films, seasons…) of a series, in the card's order. */
  relatedField() {
    const link = this.link;
    const taken = r => r.kind === link.kind && r.externalId === link.externalId || this.related.some(o => o.kind === r.kind && o.externalId === r.externalId);
    return m("div", {
      className: "Form-group MarqueeLinkModal-related"
    }, m("label", null, t('link.related_label')), m("p", {
      className: "helpText"
    }, t('link.related_help')), this.related.length ? m("ul", {
      className: "MarqueeLinkModal-relatedList"
    }, this.related.map((r, i) => m("li", null, r.poster ? m("img", {
      src: img(r.poster, 'w92'),
      alt: ""
    }) : m("span", {
      className: "Marquee-noPoster"
    }), m("span", null, r.year ? `${r.title} (${r.year})` : r.title), m(Button, {
      className: "Button Button--icon Button--link",
      icon: "fas fa-times",
      "aria-label": text('link.related_remove', {
        title: r.title
      }),
      onclick: () => this.related.splice(i, 1)
    })))) : null, m(TitlePicker, {
      wrapper: "div",
      value: this.relatedQuery,
      oninput: value => this.relatedQuery = value,
      kind: this.kind,
      linked: false,
      kindPlaceholder: text('link.related_placeholder'),
      onpick: picked => {
        if (!taken(picked)) this.related.push(picked);
        this.relatedQuery = '';
      }
    }));
  }
  onsubmit(e) {
    e.preventDefault();
    const {
      discussion
    } = this.attrs;
    const link = this.link;
    const attributes = {
      marquee: link ? {
        kind: link.kind,
        id: link.externalId,
        season: link.season || null,
        episode: link.episode ? link.episode.n : null
      } : null
    };
    if (link) attributes.marqueeRelated = this.related.map(r => ({
      kind: r.kind,
      id: r.externalId
    }));
    // Uploads are already saved; only switching back to the source's, or to none, is sent.
    const backdrops = {};
    Object.keys({
      ...this.savedBackdrops,
      ...this.backdrops
    }).forEach(id => {
      const value = this.backdrops[id] ?? null;
      if ((value === null || value === 'none') && value !== (this.savedBackdrops[id] ?? null)) backdrops[id] = value;
    });
    if (link && Object.keys(backdrops).length) attributes.marqueeBackdrops = backdrops;

    // Only rename once the episode's name is known (see oninit).
    if (link && this.rename && (!link.episode || link.episode.title)) {
      attributes.title = titleFor(link, link.season, link.episode);
    }
    this.saving = true;
    discussion.save(attributes).then(() => {
      app.alerts.show({
        type: 'success'
      }, text('link.saved'));
      this.hide();
    }, error => {
      this.saving = false;
      this.onerror(error);
    });
  }
}
flarum.reg.add('ernestdefoe-marquee', 'forum/components/LinkModal', LinkModal);